import { apiError } from '../apiError';
import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { ApiResponse } from 'types';

/**
 * ASR post-transcription auto-correction (transcriber.autocorrect). A separate LLM pass
 * that normalises noisy speech-to-text against a dictionary of canonical terms — the
 * production Yamurai agent uses it heavily for ChiShona/English code-switching.
 */

/**
 * The ringback every configured agent already points at.
 *
 * All nine agents with ringback enabled in production — Atmantan_Reception, the
 * seven IH_26 agents and Kriti — carry this exact path and no other. It is a
 * path inside the runtime container, not a URL, so it is only meaningful to
 * voicex.
 */
export const DEFAULT_RINGBACK_PATH = '/app/presets/ringback/phone_ring_1sec.wav';

export interface AutocorrectConfig {
  enabled?: boolean;
  provider?: string;              // openai | groq | gemini | azure_openai
  model?: string;
  temperature?: number;
  max_tokens?: number;
  base_url?: string;
  languages?: string[];
  primary_language?: string;
  country?: string;
  correction_rules?: string;      // the prompt + target←variant dictionary
  phoneme_corrections?: Record<string, string>;
  code_switching_words?: Record<string, string>;   // local phonetic → English
  intent_keywords?: Record<string, string[]>;
  use_history?: boolean;
  max_history_turns?: number;
  include_bot_responses?: boolean;
  detect_intent?: boolean;
  apply_before_llm?: boolean;
  fallback_on_error?: boolean;
  cache_corrections?: boolean;
  timeout?: number;
}

/** Repeated-output guard on the LLM (task_config.hallucination_detection). */
export interface HallucinationDetection {
  enabled?: boolean;
  repetition_threshold?: number;
  max_unique_word_ratio?: number;
  min_words_for_analysis?: number;
}

/** Inbound audio denoiser (input.noise_config). */
export interface NoiseConfig {
  enable?: boolean;
  method?: string;                // e.g. spectral
  aggressiveness?: number;
  highpass_cutoff?: number;
}

/**
 * AI agent management — cpass system of record. On write the backend builds the
 * agentConfig/agentPrompt and pushes the {agentUuid}/{agentUuid}_prompt binding
 * to the runtime Redis. `wsurl` is what an IVR state points at.
 */
export interface AiAgent {
  id?: number;
  agent_uuid?: string;
  organization_id?: string;
  business_id?: string;
  name: string;
  description?: string;
  enabled?: boolean;
  runtime?: 'TEEOFF_VOICE' | 'TEEOFF_LLM';
  provider?: string;
  memory_profile_id?: number;
  /**
   * Pipeline (STT→LLM→TTS) voice and language. `voice` feeds the synthesizer's
   * voice_id when tts_voice_id is unset; `language` feeds the transcriber.
   * A realtime agent uses voice_name/language_code instead — see agentMode.ts.
   */
  voice?: string;
  language?: string;
  /** Realtime (voice-to-voice) voice and language — llm_config.voice_name / language_code. */
  voice_name?: string;
  language_code?: string;
  /** Realtime only — llm_config.thinkingLevel. */
  thinking_level?: string;
  model?: string;
  temperature?: number;
  max_tokens?: number;
  greeting_message?: string;

  // ── post-call lead recovery, per agent ────────────────────────────────────
  //
  // A realtime agent captures the lead by invoking its tool DURING the call.
  // When the caller hangs up first, it never fires — so cpass can extract one
  // from the transcript instead. These decide whether it does, and with whose
  // account.
  //
  // Every one of them is OPTIONAL and undefined means "use the deployment
  // default", which is why the form leaves a field alone rather than sending an
  // empty string for it.

  /** IF_MISSING (default) · OFF · ALWAYS. */
  post_call_lead_mode?: 'IF_MISSING' | 'OFF' | 'ALWAYS';
  /**
   * What the runtime is told to summarise at the end of a call.
   *
   * Was write-only: cpass set it to "make summary" whenever it built a hook and
   * never read it back, so a customer's own extraction instructions lived in a
   * field nothing displayed and nothing could edit. One agent's were a page
   * long and were destroyed by a push, unrecoverably.
   *
   * It is also where the fields come from when an agent has no lead tool.
   */
  post_call_summary_prompt?: string;
  /**
   * What every lead from this agent reports as its Source, in cpass AND in the
   * CRM.
   *
   * Unset keeps today's behaviour, which is the reason this exists: there were
   * FOUR hardcoded values for one channel. An in-call capture reported
   * "Voice Agent", or whatever the agent's own tool payload sent — "Voice AI" in
   * production — while the recovery of the same kind of call reported
   * "post-call recovery" in cpass and "Voice Agent (post-call)" to the CRM. One
   * customer's Zoho split one channel across two Lead_Source values, neither of
   * them chosen by that customer.
   *
   * Set here, it wins over the agent's payload. Which ROUTE a lead took is a
   * separate fact and is already recorded in `origin` — a column cpass writes
   * and no payload can reach. Source is a label; origin is the dimension.
   */
  lead_source?: string | null;
  /**
   * Fields a lead must carry before it may be pushed to the CRM.
   *
   * A JSON array or a comma-separated string. Empty or unset requires nothing,
   * which is where every existing agent is.
   *
   * A lead missing one of these is written in cpass as usual and marked
   * INCOMPLETE, with the missing names in `syncError`; nothing is sent and
   * nothing is queued. The CRM push is an upsert matched on the phone number,
   * so a record thinner than this list merges into the good one rather than
   * sitting beside it — which is how 14 nameless leads reached one customer's
   * Zoho before this existed.
   */
  crm_required_fields?: string | string[] | null;
  /** What to call a caller whose name we do not have. Null = the platform default. */
  caller_name_fallback?: string | null;
  lead_extraction_enabled?: boolean | null;
  /** OPENAI | ANTHROPIC | GEMINI | AZURE_OPENAI | OPENAI_COMPATIBLE. Null inherits the platform. */
  lead_extraction_provider?: string | null;
  lead_extraction_model?: string | null;
  lead_extraction_base_url?: string | null;
  /**
   * WRITE-ONLY. The server never returns it, so this is only ever set when
   * somebody types a new one; a blank means "leave the stored key alone", not
   * "clear it" — the box cannot show the current value, so empty is the normal
   * state of an unchanged secret.
   */
  lead_extraction_api_key?: string;
  /** Read-only: whether a key is stored, so the screen can say so without showing it. */
  lead_extraction_api_key_set?: boolean;
  system_prompt?: string;
  knowledge_base?: string;
  kb_enabled?: boolean;
  kb_top_k?: number;
  kb_similarity_threshold?: number;
  kb_embedding_model?: string;
  // Voice runtime engine config (voicex tasks[].tools_config). Null => voicex defaults.
  stt_provider?: string;
  stt_model?: string;
  tts_provider?: string;
  tts_voice_id?: string;
  tts_model?: string;
  /**
   * The mode markers. The backend decides whether to build a realtime document
   * from agent_flow_type, falling back to llm_agent_type
   * (AiAgentService.isRealtimeFlow) — NOT from agent_type.
   */
  agent_flow_type?: string;   // 'streaming' | 'non_streaming' | 'gemini_realtime'
  llm_agent_type?: string;    // llm_agent.agent_type: 'simple_llm_agent' | 'gemini_realtime'
  /**
   * The OUTER voicex agent_type, which the backend defaults to the literal
   * "other". It is not the pipeline mode, however much it looks like one.
   */
  agent_type?: string;
  hangup_after_silence?: number;
  call_terminate?: number;
  // Tuning params surfaced by the catalog-driven config editor (voicex task_config).
  interruption_backoff_period?: number;
  use_fillers?: boolean;
  // Boolean in the runtime (Optional[bool]); widened from number to accept both so an
  // older numeric value still loads.
  optimize_latency?: boolean | number;
  incremental_delay?: number;
  // Realtime conversation config (voicex task_config). conversation_config = free-form override.
  agent_speaks_first?: boolean;
  check_if_user_online?: boolean;
  check_user_online_message?: string;
  trigger_user_online_message_after?: number;
  number_of_words_for_interruption?: number;
  backchanneling?: boolean;
  ambient_noise?: boolean;
  ambient_noise_track?: string;
  conversation_config?: Record<string, any>;

  // ── FULL voicex config surface (Yamurai-grade agent builder) ──────────────
  // Every field below is optional and passed through to config_json under the
  // exact name the runtime expects. Undefined => the deployment default; the form
  // leaves an absent field alone rather than sending an empty value for it.

  // LLM (llm_config)
  family?: string;
  top_k?: number;
  top_p?: number;
  min_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  base_url?: string;

  // STT (transcriber)
  endpointing?: number;
  transcriber_keywords?: string;   // transcriber.keywords — comma-separated STT boost terms
  sampling_rate?: number;
  encoding?: string;
  autocorrect?: AutocorrectConfig; // transcriber.autocorrect

  // TTS (synthesizer)
  audio_format?: string;
  buffer_size?: number;
  caching?: boolean;
  similarity_boost?: number;
  tts_temperature?: number;

  // Conversation / interruption (task_config) — extended
  enable_signal_based_interruption?: boolean;
  enable_speech_final_interrupt?: boolean;
  backchanneling_message_gap?: number;
  backchanneling_start_delay?: number;
  hangup_after_LLMCall?: boolean;
  check_user_online_message_repeat_last?: boolean;
  call_cancellation_prompt?: string;
  // interruption classifier + phrase dictionaries
  interruption_classifier_enabled?: boolean;
  interruption_classifier_dedup_window_s?: number;
  interruption_classifier_per_call_cap?: number;
  post_completion_ack_recency_ms?: number;
  combine_interrupted_user_messages?: boolean;
  directive_phrases?: string[];
  post_completion_ack_phrases?: string[];
  backchannel_phrases?: string[];
  keepalive_phrases?: string[];
  hallucination_detection?: HallucinationDetection;
  enable_transcript_buffering?: boolean;
  utterance_debounce_ms?: number;

  // DTMF / keypad
  keypad_message?: string;              // → llm_meta_data.keypadMessage
  default_keypad_input_message?: string; // → transcriber_meta_data.default_keypad_input_message

  // Audio IO
  input_provider?: string;
  output_provider?: string;
  io_format?: string;
  io_sampling_rate?: number;
  noise_config?: NoiseConfig;

  // Fillers — use_fillers lives in the interruption block above; this is the global
  // filler phrase the runtime speaks while it thinks. Per-tool filler messages live
  // on each tool's execution config, not here.
  filler_message?: string;

  // Ringback / realtime VAD (voicex gemini_live block: ringback tone, welcome delay,
  // and voice-activity-detection sensitivities for realtime voice-to-voice agents).
  //
  // play_ringback is the switch and ringback the file it plays. The editor used
  // to offer the file alone, which turned nothing on: the runtime defaults
  // play_ringback to false and goes straight to the welcome bootstrap, so a
  // path typed here never played. With the switch on and no file, the runtime
  // synthesises a 400 Hz India-PSTN ring rather than failing.
  play_ringback?: boolean;
  ringback?: string;
  welcome_delay?: number;
  // The runtime plays its ringback inside the welcome bootstrap, and skips the
  // bootstrap entirely when no greeting is configured — so an agent with no
  // greeting never rings, whatever play_ringback says. This is the greeting
  // that does NOT force an exact spoken line (unlike greeting_message, sent as
  // "Begin by saying exactly: ..."), so an agent whose opening interpolates a
  // name can still have one.
  welcome_instruction?: string;
  vad_model?: string;
  vad_offset?: number;
  vad_onset?: number;
  vad_threshold?: number;
  vad_filter?: boolean;
  vad_timeout?: number;
  speech_end_timeout?: number;
  enable_vad_events?: boolean;

  wsurl?: string;
  /**
   * Set on agents taken over from the runtime. Their own document is preserved
   * and only a short list of fields is overlaid onto it on save, so most of
   * this editor is recorded in cpass without reaching the runtime.
   */
  adopted_from_runtime?: boolean;
  runtime_document?: Record<string, any>;
  /**
   * Custom API push binding(s) — `{providerId, values, enabled?}` or an array of
   * them (CustomApiPushService.bindings). Read from config_json by the backend;
   * `values` are the template's {{agent.*}}. Edited only through
   * CustomApiPushSection, whose save changes this key and nothing else.
   */
  custom_api_push?: unknown;
  /** ai_agents.crm_sync_disabled: nothing leaves cpass for this agent — Custom API pushes included. */
  crm_sync_disabled?: boolean;
  runtime_synced?: boolean;
  runtime_message?: string;
  created_at?: string;
  updated_at?: string;
}

/** A summary of an agent that lives on the runtime but not in cpass. */
export interface RuntimeAgent {
  agent_uuid: string;
  business_id?: string;
  agent_name?: string | null;
  created_at?: number;
  updated_at?: number;
  managed_in_cpass?: boolean;
}

export interface AgentTool {
  id?: number;
  agent_id?: number;
  name: string;
  description?: string;
  language?: string;
  parameters?: Record<string, any>;
  execution_type?: 'api' | 'static' | 'knowledge_base' | 'plugin';
  execution_config?: Record<string, any>;
  enabled?: boolean;
  sort_order?: number;
}

export interface KnowledgeDocument {
  id?: number;
  agent_id?: number;
  filename: string;
  content?: string;
  storage_ref?: string;
  status?: string;
  created_at?: string;
}

export interface AgentPrompt {
  id?: number;
  agent_id?: number;
  language: string;
  system_prompt?: string;
  greeting_message?: string;
}

export type ChannelType = 'VOICE' | 'CHAT' | 'WHATSAPP' | 'WHATSAPP_CALL' | 'RCS' | 'EMAIL';
export interface AgentChannel {
  id?: number;
  agent_id?: number;
  channel: ChannelType;
  enabled?: boolean;
  channel_config?: Record<string, any>;
  binding_ref?: string;
  status?: string;
}

// Shared so a 403 reads as a refusal rather than a load failure.
const err = (e: any, msg: string) => apiError(e, msg);

export const aiAgentsService = {
  list: async (organizationId?: string): Promise<ApiResponse<AiAgent[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.AI_AGENTS, { params: organizationId ? { organizationId } : {} })).data; }
    catch (e: any) { return err(e, 'Failed to load agents'); }
  },
  get: async (id: number): Promise<ApiResponse<AiAgent>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.AI_AGENT_DETAIL(id))).data; }
    catch (e: any) { return err(e, 'Failed to load agent'); }
  },
  create: async (body: AiAgent, organizationId?: string): Promise<ApiResponse<AiAgent>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.AI_AGENTS, body, { params: organizationId ? { organizationId } : {} })).data; }
    catch (e: any) { return err(e, 'Failed to create agent'); }
  },
  update: async (id: number, body: AiAgent): Promise<ApiResponse<AiAgent>> => {
    try { return (await axiosInstance.put(API_ENDPOINTS.AI_AGENT_DETAIL(id), body)).data; }
    catch (e: any) { return err(e, 'Failed to update agent'); }
  },
  /**
   * Move an agent to another business.
   *
   * Its own call rather than a field on update(): the editor sends the whole
   * agent back on every save, and changing who owns a record must not be a side
   * effect of renaming it.
   *
   * The response reports how many captures and call events STAYED with the old
   * business — they carry their own organisation, recording where a lead was
   * actually filed, and are deliberately not rewritten.
   */
  move: async (id: number, organizationId: string): Promise<ApiResponse<{
    moved: boolean; from: string; to: string;
    capturesLeftBehind: number; callEventsLeftBehind: number;
  }>> => {
    try { return (await axiosInstance.post(`${API_ENDPOINTS.AI_AGENT_DETAIL(id)}/move`, { organizationId })).data; }
    catch (e: any) { return err(e, 'Failed to move the agent'); }
  },

  remove: async (id: number): Promise<ApiResponse<void>> => {
    try { return (await axiosInstance.delete(API_ENDPOINTS.AI_AGENT_DETAIL(id))).data; }
    catch (e: any) { return err(e, 'Failed to delete agent'); }
  },

  /**
   * Agents that exist on the voice runtime but have no cpass record — the ones
   * created directly against the runtime, which the normal list cannot show
   * because it only reads cpass's own registry.
   *
   * businessId is optional: omitted, the backend uses the business the org's
   * provider is bound to. Only finds agents created since the runtime started
   * recording which business an agent belongs to — older ones carry no business
   * reference at all and are not discoverable.
   */
  listRuntimeOnly: async (businessId?: string, limit = 200, offset = 0): Promise<ApiResponse<RuntimeAgent[]>> => {
    try {
      return (await axiosInstance.get(`${API_ENDPOINTS.AI_AGENTS}/runtime`, {
        params: { ...(businessId ? { business_id: businessId } : {}), limit, offset },
      })).data;
    } catch (e: any) { return err(e, 'Failed to load agents from the runtime'); }
  },

  /** Full config of a runtime agent that has no cpass record yet. */
  viewRuntimeAgent: async (agentUuid: string): Promise<ApiResponse<Record<string, any>>> => {
    try { return (await axiosInstance.get(`${API_ENDPOINTS.AI_AGENTS}/runtime/${encodeURIComponent(agentUuid)}`)).data; }
    catch (e: any) { return err(e, 'Failed to load the agent from the runtime'); }
  },

  /**
   * Take over a runtime-only agent: creates the cpass record from the runtime's
   * own config, keeping the same agentUuid, after which the normal editor works.
   * Does not re-push to the runtime, so adopting cannot alter a live agent.
   */
  adoptRuntimeAgent: async (agentUuid: string): Promise<ApiResponse<AiAgent>> => {
    try { return (await axiosInstance.post(`${API_ENDPOINTS.AI_AGENTS}/runtime/${encodeURIComponent(agentUuid)}/adopt`)).data; }
    catch (e: any) { return err(e, 'Failed to adopt the agent'); }
  },

  /**
   * Push a runtime document to the agent VERBATIM.
   *
   * Deliberately not the normal edit path. The ordinary save overlays only the
   * handful of fields cpass models onto the stored document, which is what
   * keeps an adopted agent's config from being silently rebuilt — but it also
   * means model, voice, language and the whole gemini_live block cannot be
   * changed that way at all. This is the only route to them, and the caller
   * owns the correctness of the whole document.
   *
   * The cpass record is left alone by the backend: restoring the runtime and
   * re-adopting are separate decisions.
   */
  pushRuntimeDocument: async (id: number, document: Record<string, any>): Promise<ApiResponse<Record<string, any>>> => {
    try { return (await axiosInstance.post(`${API_ENDPOINTS.AI_AGENT_DETAIL(id)}/runtime-document`, document)).data; }
    catch (e: any) { return err(e, 'Failed to push the runtime document'); }
  },
};

export const agentSettingsService = {
  getDeveloperMode: async (): Promise<ApiResponse<{ developerMode: boolean }>> => {
    try { return (await axiosInstance.get('/agents/developer-mode')).data; }
    catch (e: any) { return err(e, 'Failed to load developer mode'); }
  },
  setDeveloperMode: async (developerMode: boolean): Promise<ApiResponse<{ developerMode: boolean }>> => {
    try { return (await axiosInstance.put('/agents/developer-mode', { developerMode })).data; }
    catch (e: any) { return err(e, 'Failed to set developer mode'); }
  },
  toolCatalog: async (): Promise<ApiResponse<AgentTool[]>> => {
    try { return (await axiosInstance.get('/agents/tools/catalog')).data; }
    catch (e: any) { return err(e, 'Failed to load tool catalog'); }
  },
};

export const agentToolsService = {
  list: async (agentId: number): Promise<ApiResponse<AgentTool[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.AI_AGENT_TOOLS(agentId))).data; }
    catch (e: any) { return err(e, 'Failed to load tools'); }
  },
  upsert: async (agentId: number, body: AgentTool): Promise<ApiResponse<AgentTool>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.AI_AGENT_TOOLS(agentId), body)).data; }
    catch (e: any) { return err(e, 'Failed to save tool'); }
  },
  remove: async (agentId: number, toolId: number): Promise<ApiResponse<void>> => {
    try { return (await axiosInstance.delete(API_ENDPOINTS.AI_AGENT_TOOL_DETAIL(agentId, toolId))).data; }
    catch (e: any) { return err(e, 'Failed to delete tool'); }
  },
};

export const agentDocumentsService = {
  list: async (agentId: number): Promise<ApiResponse<KnowledgeDocument[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.AI_AGENT_DOCS(agentId))).data; }
    catch (e: any) { return err(e, 'Failed to load documents'); }
  },
  add: async (agentId: number, body: KnowledgeDocument): Promise<ApiResponse<KnowledgeDocument>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.AI_AGENT_DOCS(agentId), body)).data; }
    catch (e: any) { return err(e, 'Failed to add document'); }
  },
  remove: async (agentId: number, docId: number): Promise<ApiResponse<void>> => {
    try { return (await axiosInstance.delete(API_ENDPOINTS.AI_AGENT_DOC_DETAIL(agentId, docId))).data; }
    catch (e: any) { return err(e, 'Failed to delete document'); }
  },
};

export const agentPromptsService = {
  list: async (agentId: number): Promise<ApiResponse<AgentPrompt[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.AI_AGENT_PROMPTS(agentId))).data; }
    catch (e: any) { return err(e, 'Failed to load prompts'); }
  },
  upsert: async (agentId: number, body: AgentPrompt): Promise<ApiResponse<AgentPrompt>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.AI_AGENT_PROMPTS(agentId), body)).data; }
    catch (e: any) { return err(e, 'Failed to save prompt'); }
  },
  remove: async (agentId: number, promptId: number): Promise<ApiResponse<void>> => {
    try { return (await axiosInstance.delete(API_ENDPOINTS.AI_AGENT_PROMPT_DETAIL(agentId, promptId))).data; }
    catch (e: any) { return err(e, 'Failed to delete prompt'); }
  },
};

export const agentChannelsService = {
  list: async (agentId: number): Promise<ApiResponse<AgentChannel[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.AI_AGENT_CHANNELS(agentId))).data; }
    catch (e: any) { return err(e, 'Failed to load channels'); }
  },
  upsert: async (agentId: number, body: AgentChannel): Promise<ApiResponse<AgentChannel>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.AI_AGENT_CHANNELS(agentId), body)).data; }
    catch (e: any) { return err(e, 'Failed to save channel'); }
  },
  remove: async (agentId: number, channelId: number): Promise<ApiResponse<void>> => {
    try { return (await axiosInstance.delete(API_ENDPOINTS.AI_AGENT_CHANNEL_DETAIL(agentId, channelId))).data; }
    catch (e: any) { return err(e, 'Failed to delete channel'); }
  },
};
